import { useCallback, useRef, useState } from 'react';

/**
 * Reusable click-to-copy state. `copy(value, key?)` writes to the clipboard and marks
 * `key` (defaults to the value) as copied for ~1.8s so a button can swap its icon/label.
 * Keyed, so many copyable items on one page track independently. No i18n/toast here —
 * the caller (e.g. CopyButton) owns the feedback UI.
 */
export function useCopy(resetMs = 1800): {
  copy: (value: string, key?: string) => Promise<boolean>;
  copied: string | null;
  isCopied: (key: string) => boolean;
} {
  const [copied, setCopied] = useState<string | null>(null);
  const timer = useRef<number>();

  const copy = useCallback(async (value: string, key?: string): Promise<boolean> => {
    const k = key ?? value;
    try {
      await navigator.clipboard?.writeText(value);
    } catch {
      return false;
    }
    setCopied(k);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopied((c) => (c === k ? null : c)), resetMs);
    return true;
  }, [resetMs]);

  const isCopied = useCallback((key: string): boolean => copied === key, [copied]);

  return { copy, copied, isCopied };
}
